import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { 
  Building2, Users, FileText, CheckCircle, ArrowRight, 
  Scale, BookOpen, Clock, Shield, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const SyndicBenevole = () => {
  const tableOfContents = [
    { id: "definition", title: "Qu'est-ce qu'un syndic bénévole ?" },
    { id: "avantages", title: "Les avantages du syndic bénévole" },
    { id: "conditions", title: "Conditions pour devenir syndic bénévole" },
    { id: "demarches", title: "Démarches pour devenir syndic bénévole" },
    { id: "obligations", title: "Obligations légales du syndic bénévole" },
    { id: "outils", title: "Outils indispensables" },
    { id: "faq", title: "FAQ" },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Syndic Bénévole : Guide Complet 2025 | Comment devenir syndic bénévole</title>
        <meta name="description" content="Guide complet sur le syndic bénévole en copropriété. Découvrez comment devenir syndic bénévole, les obligations légales, les avantages et les outils indispensables pour gérer votre copropriété." />
        <meta name="keywords" content="syndic bénévole, devenir syndic bénévole, syndic bénévole copropriété, syndic non professionnel, gestion copropriété bénévole, obligations syndic bénévole" />
        <link rel="canonical" href="https://copropriete.app/syndic-benevole" />
      </Helmet>

      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900">copropriete.app</span>
            </Link>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => window.location.href = '/login'}>
                Se connecter
              </Button>
              <Button onClick={() => window.location.href = '/login'} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                C'est gratuit
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <BookOpen className="w-4 h-4" />
              Guide complet 2025
            </span>
            <h1 className="text-4xl sm:text-5xl font-black text-slate-900 mb-6">
              Syndic Bénévole : Le Guide Ultime
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Tout ce que vous devez savoir pour devenir syndic bénévole et gérer efficacement votre copropriété sans syndic professionnel.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Sidebar - Table of Contents */}
          <aside className="lg:w-72 flex-shrink-0">
            <div className="sticky top-24 bg-slate-50 rounded-2xl p-6">
              <h3 className="font-bold text-slate-900 mb-4">Sommaire</h3>
              <nav className="space-y-2">
                {tableOfContents.map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    className="flex items-center gap-2 text-slate-600 hover:text-emerald-600 transition-colors text-sm py-1"
                  >
                    <ChevronRight className="w-4 h-4" />
                    {item.title}
                  </a>
                ))}
              </nav>
              
              <div className="mt-8 p-4 bg-emerald-100 rounded-xl">
                <p className="text-sm text-emerald-800 font-medium mb-3">
                  Gérez votre copropriété simplement avec copropriete.app
                </p>
                <Button size="sm" onClick={() => window.location.href = '/login'} className="w-full bg-emerald-600 hover:bg-emerald-700">
                  C'est gratuit 7 jours
                </Button>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 prose prose-slate prose-lg max-w-none">
            <section id="definition" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Users className="w-8 h-8 text-emerald-600" />
                Qu'est-ce qu'un syndic bénévole ?
              </h2>
              <p>
                Le <strong>syndic bénévole</strong>, aussi appelé <strong>syndic non professionnel</strong>, est un copropriétaire qui assure la gestion de la copropriété sans être un professionnel de l'immobilier. Contrairement au syndic professionnel, il ne perçoit généralement pas de rémunération ou une rémunération très limitée.
              </p>
              <p>
                La loi du 10 juillet 1965 et son décret d'application permettent à tout copropriétaire d'être élu syndic de sa copropriété. C'est une solution particulièrement adaptée aux <strong>petites et moyennes copropriétés</strong> (moins de 50 lots) qui souhaitent :
              </p>
              <ul>
                <li>Réduire significativement les charges de copropriété</li>
                <li>Avoir une gestion plus réactive et transparente</li>
                <li>Garder le contrôle sur les décisions importantes</li>
              </ul>

              <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-xl my-8">
                <p className="text-emerald-900 font-medium m-0">
                  💡 <strong>Bon à savoir :</strong> Selon la loi ALUR de 2014, les petites copropriétés (moins de 16 lots) peuvent adopter un fonctionnement simplifié, facilitant encore plus la gestion en syndic bénévole.
                </p>
              </div>
            </section>

            <section id="avantages" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
                Les avantages du syndic bénévole
              </h2>
              
              <h3 className="text-xl font-bold text-slate-800">1. Économies substantielles</h3>
              <p>
                Le principal avantage est financier. Les honoraires d'un syndic professionnel peuvent représenter <strong>150€ à 300€ par lot et par an</strong>. Pour une copropriété de 20 lots, cela représente 3 000€ à 6 000€ d'économies annuelles.
              </p>

              <h3 className="text-xl font-bold text-slate-800">2. Réactivité et proximité</h3>
              <p>
                Vivant sur place ou à proximité, le syndic bénévole peut réagir rapidement en cas de problème (fuite d'eau, panne d'ascenseur, etc.). La communication avec les copropriétaires est directe et fluide.
              </p>

              <h3 className="text-xl font-bold text-slate-800">3. Transparence totale</h3>
              <p>
                Les comptes sont gérés en toute transparence, sans frais cachés ni commissions sur les travaux. Chaque copropriétaire peut facilement accéder aux documents et comprendre la gestion de sa copropriété.
              </p>

              <h3 className="text-xl font-bold text-slate-800">4. Décisions adaptées</h3>
              <p>
                Le syndic bénévole connaît parfaitement sa copropriété et peut prendre des décisions vraiment adaptées aux besoins des résidents, sans rechercher systématiquement le profit.
              </p>
            </section>

            <section id="conditions" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Scale className="w-8 h-8 text-emerald-600" />
                Conditions pour devenir syndic bénévole
              </h2>
              
              <p>Pour devenir syndic bénévole, vous devez remplir les conditions suivantes :</p>
              
              <ul>
                <li><strong>Être copropriétaire</strong> dans l'immeuble concerné (posséder au moins un lot)</li>
                <li><strong>Être majeur</strong> et jouir de tous ses droits civils</li>
                <li><strong>Ne pas être interdit de gérer</strong> par décision de justice</li>
                <li><strong>Être élu</strong> par l'assemblée générale à la majorité de l'article 25</li>
              </ul>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-xl my-8">
                <p className="text-yellow-900 font-medium m-0">
                  ⚠️ <strong>Important :</strong> Contrairement au syndic professionnel, le syndic bénévole n'a pas besoin de carte professionnelle, de garantie financière, ni d'assurance de responsabilité civile professionnelle obligatoire (bien que cette dernière soit fortement recommandée).
                </p>
              </div>
            </section>

            <section id="demarches" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <FileText className="w-8 h-8 text-emerald-600" />
                Démarches pour devenir syndic bénévole
              </h2>

              <h3 className="text-xl font-bold text-slate-800">Étape 1 : Préparer sa candidature</h3>
              <p>
                Rédigez une lettre de candidature expliquant vos motivations et votre projet pour la copropriété. Joignez un projet de contrat de syndic (durée, éventuelles indemnités, etc.).
              </p>

              <h3 className="text-xl font-bold text-slate-800">Étape 2 : Inscrire le point à l'ordre du jour</h3>
              <p>
                Demandez au syndic actuel d'inscrire la désignation d'un nouveau syndic à l'ordre du jour de la prochaine AG. Cette demande doit être faite par lettre recommandée.
              </p>

              <h3 className="text-xl font-bold text-slate-800">Étape 3 : Vote en assemblée générale</h3>
              <p>
                L'élection du syndic bénévole se fait à la majorité de l'article 25 (majorité des voix de tous les copropriétaires). Si cette majorité n'est pas atteinte mais que le projet recueille au moins 1/3 des voix, un second vote à la majorité simple peut être organisé.
              </p>

              <h3 className="text-xl font-bold text-slate-800">Étape 4 : Transition avec l'ancien syndic</h3>
              <p>
                L'ancien syndic dispose d'un mois pour transmettre tous les documents et fonds de la copropriété au nouveau syndic bénévole.
              </p>
            </section>

            <section id="obligations" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Shield className="w-8 h-8 text-emerald-600" />
                Obligations légales du syndic bénévole
              </h2>

              <p>Le syndic bénévole a les mêmes obligations légales qu'un syndic professionnel :</p>

              <h3 className="text-xl font-bold text-slate-800">Gestion administrative</h3>
              <ul>
                <li>Tenir à jour la liste des copropriétaires</li>
                <li>Conserver les archives de la copropriété</li>
                <li>Immatriculer la copropriété au registre national</li>
                <li>Établir et mettre à jour le carnet d'entretien</li>
              </ul>

              <h3 className="text-xl font-bold text-slate-800">Gestion financière</h3>
              <ul>
                <li>Ouvrir un compte bancaire séparé au nom du syndicat</li>
                <li>Établir le budget prévisionnel</li>
                <li>Appeler les charges et gérer les impayés</li>
                <li>Tenir une comptabilité en partie double</li>
              </ul>

              <h3 className="text-xl font-bold text-slate-800">Assemblées générales</h3>
              <ul>
                <li>Convoquer l'AG au moins une fois par an</li>
                <li>Rédiger et envoyer les convocations avec l'ordre du jour</li>
                <li>Rédiger le procès-verbal dans les 2 mois suivant l'AG</li>
                <li>Exécuter les décisions votées</li>
              </ul>
            </section>

            <section id="outils" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Clock className="w-8 h-8 text-emerald-600" />
                Outils indispensables pour le syndic bénévole
              </h2>

              <p>
                Pour gérer efficacement une copropriété en tant que syndic bénévole, vous avez besoin d'outils adaptés :
              </p>

              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 my-8 border border-emerald-200">
                <h3 className="text-2xl font-bold text-slate-900 mb-4">copropriete.app : la solution idéale</h3>
                <p className="text-slate-700 mb-6">
                  Notre plateforme a été conçue spécifiquement pour les syndics bénévoles. Elle vous permet de :
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span>Gérer vos copropriétaires et leurs tantièmes</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span>Organiser vos assemblées générales facilement</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span>Créer et suivre les appels de fonds automatiquement</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span>Stocker tous vos documents en sécurité</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span>Poser vos questions juridiques à notre assistant IA</span>
                  </li>
                </ul>
                <Button onClick={() => window.location.href = '/login'} size="lg" className="bg-emerald-600 hover:bg-emerald-700">
                  Essayer gratuitement pendant 7 jours
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </section>

            <section id="faq" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Questions fréquentes sur le syndic bénévole
              </h2>

              <div className="space-y-6">
                <div className="border border-slate-200 rounded-xl p-6">
                  <h3 className="font-bold text-slate-900 mb-2">Le syndic bénévole est-il rémunéré ?</h3>
                  <p className="text-slate-600 m-0">
                    Le syndic bénévole peut percevoir une indemnité votée en AG, mais il n'est pas obligé d'être rémunéré. Cette indemnité couvre généralement les frais engagés (téléphone, déplacements, etc.).
                  </p>
                </div>

                <div className="border border-slate-200 rounded-xl p-6">
                  <h3 className="font-bold text-slate-900 mb-2">Quelle est la durée du mandat ?</h3>
                  <p className="text-slate-600 m-0">
                    Le mandat du syndic bénévole est de 3 ans maximum, renouvelable. Il peut aussi être désigné pour une durée plus courte.
                  </p>
                </div>

                <div className="border border-slate-200 rounded-xl p-6">
                  <h3 className="font-bold text-slate-900 mb-2">Le syndic bénévole a-t-il besoin d'une assurance ?</h3>
                  <p className="text-slate-600 m-0">
                    Bien que non obligatoire, une assurance responsabilité civile est fortement recommandée. Elle protège le syndic bénévole en cas d'erreur de gestion causant un préjudice à la copropriété.
                  </p>
                </div>

                <div className="border border-slate-200 rounded-xl p-6">
                  <h3 className="font-bold text-slate-900 mb-2">Peut-on revenir à un syndic professionnel ?</h3>
                  <p className="text-slate-600 m-0">
                    Oui, à tout moment l'assemblée générale peut décider de ne pas renouveler le mandat du syndic bénévole et d'élire un syndic professionnel à la place.
                  </p>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>

      {/* CTA Final */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Prêt à devenir syndic bénévole ?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            copropriete.app vous accompagne dans toutes vos démarches de gestion.
          </p>
          <Button 
            size="lg"
            onClick={() => window.location.href = '/login'}
            className="bg-emerald-500 hover:bg-emerald-400 text-white text-lg px-10 py-6 rounded-xl"
          >
            Commencer l'gratuit
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-slate-950 text-slate-400">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-white">copropriete.app</span>
          </Link>
          <p className="text-sm">© 2025 copropriete.app. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default SyndicBenevole;
